/**
 * Iter88 — Remove OT hours dialog.
 *
 * Opens from the Attendance row's three-dot menu → OVERTIME →
 * "Remove OT hours…" (sibling of "Add OT hours…"). Only shown when
 * the row has at least one authorized OT entry.
 *
 * Modes (operator picks per-click):
 *   • per-entry → DELETE /api/attendance/daily/{daily_id}/ot-entries/{entry_id}
 *   • remove-all → DELETE /api/attendance/daily/{daily_id}/ot-entries
 *
 * Contract shared across both paths (Iter88 server contract):
 *   - mandatory `reason` ≥ 5 chars (same as Adjust-Hours)
 *   - 409 on approved/locked periods unless the caller is Super Admin
 *     AND `super_admin_override=true`
 *   - full before/after audit snapshot via `attendance_ot_removed` or
 *     `attendance_ot_removed_super_admin_override`
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Minus, Trash2 } from "lucide-react";

import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function _fmtHM(mins) {
    const m = Math.max(0, Number(mins) || 0);
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h === 0) return `${r}m`;
    if (r === 0) return `${h}h`;
    return `${h}h ${r}m`;
}

function _fmtTs(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function OTRemoveDialog({ open, row, onClose, onSaved }) {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "kaafi_system_admin";
    const [reason, setReason] = useState("");
    const [override, setOverride] = useState(false);
    const [busy, setBusy] = useState(false);
    // Highlights the specific entry that failed on a per-entry call.
    const [activeId, setActiveId] = useState(null);

    // Reset when the dialog opens / changes row.
    React.useEffect(() => {
        if (!open) return;
        setReason("");
        setOverride(false);
        setActiveId(null);
    }, [open, row?.id]);

    const entries = useMemo(() => {
        const live = (row?.ot_entries || []).filter((e) => !e.superseded_at);
        // Newest first — matches the Audit Log viewer convention.
        return [...live].sort(
            (a, b) => (b.authorized_at || "").localeCompare(a.authorized_at || ""),
        );
    }, [row?.ot_entries]);

    const periodLocked = row?.status === "approved" || row?.status === "locked";
    const reasonValid = reason.trim().length >= 5;
    const canSubmit = reasonValid && !busy
        && (!periodLocked || (isSuperAdmin && override));

    const _why = () => {
        if (!reasonValid) {
            if (reason.trim().length === 0) {
                return "Add a reason (min 5 characters) to continue.";
            }
            return `Reason needs at least 5 characters (${reason.trim().length}/5).`;
        }
        if (periodLocked && !isSuperAdmin) {
            return "Period approved — contact a Super Admin to remove OT.";
        }
        if (periodLocked && !override) {
            return "Tick the Super Admin override to proceed on an approved period.";
        }
        if (busy) return "Working…";
        return "";
    };

    if (!row) return null;

    const _callDelete = async (url) => {
        setBusy(true);
        try {
            const body = {
                reason: reason.trim(),
                super_admin_override: !!(periodLocked && isSuperAdmin && override),
            };
            const { data } = await api.delete(url, { data: body });
            return data;
        } finally {
            setBusy(false);
        }
    };

    const removeOne = async (entry) => {
        if (!canSubmit) return;
        setActiveId(entry.id);
        try {
            const data = await _callDelete(
                `/attendance/daily/${row.id}/ot-entries/${entry.id}`,
            );
            toast.success(`Removed ${_fmtHM(entry.minutes)} of OT.`);
            onSaved && onSaved(data);
            // Keep the dialog open if there are more entries, close otherwise.
            if ((entries.length - 1) <= 0) {
                onClose && onClose();
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setActiveId(null);
        }
    };

    const removeAll = async () => {
        if (!canSubmit) return;
        try {
            const data = await _callDelete(
                `/attendance/daily/${row.id}/ot-entries`,
            );
            const n = data?.removed_count ?? entries.length;
            toast.success(
                `Removed all authorized OT (${n} entr${n === 1 ? "y" : "ies"}).`,
            );
            onSaved && onSaved(data);
            onClose && onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose && onClose()}>
            <DialogContent
                className="max-w-2xl"
                data-testid="ot-remove-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <Minus className="h-5 w-5 text-[color:var(--asl-crimson)]" />
                        Remove OT hours
                    </DialogTitle>
                    <DialogDescription>
                        {row.employee_name || row.employee_id} · {row.work_date}
                        {" · "}
                        Authorized total:{" "}
                        <span className="font-semibold">
                            {_fmtHM(row.authorized_ot_minutes || 0)}
                        </span>
                    </DialogDescription>
                </DialogHeader>

                {entries.length === 0 ? (
                    <div
                        className="text-sm text-[color:var(--asl-muted)] italic py-6 text-center border border-dashed border-[color:var(--asl-border)] rounded-md"
                        data-testid="ot-remove-empty"
                    >
                        No authorized OT entries on this row.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {entries.map((e) => (
                            <div
                                key={e.id}
                                className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
                                style={{ borderColor: "var(--asl-border)" }}
                                data-testid={`ot-remove-entry-${e.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span
                                            className="font-semibold asl-navy-text mono"
                                            data-testid={`ot-remove-entry-${e.id}-minutes`}
                                        >
                                            {_fmtHM(e.minutes)}
                                        </span>
                                        <span className="text-xs text-[color:var(--asl-muted)]">
                                            × {Number(e.multiplier || 1).toFixed(2)}
                                        </span>
                                        {e.source && (
                                            <span className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                                {e.source}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-[color:var(--asl-muted)] truncate">
                                        {e.reason || "—"}
                                    </div>
                                    <div className="text-[10px] text-[color:var(--asl-muted)]">
                                        {e.authorized_by_name || e.authorized_by_user_id || "—"}
                                        {" · "}
                                        {_fmtTs(e.authorized_at)}
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-[color:var(--asl-crimson)] border-rose-200 hover:bg-rose-50"
                                    disabled={!canSubmit || activeId === e.id}
                                    onClick={() => removeOne(e)}
                                    data-testid={`ot-remove-entry-${e.id}-btn`}
                                >
                                    {activeId === e.id ? "Removing…" : "Remove"}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="space-y-1">
                    <Label htmlFor="ot-remove-reason" className="text-xs">
                        Reason <span className="text-rose-600">*</span>{" "}
                        <span className="text-[10px] text-[color:var(--asl-muted)]">
                            (min 5 chars — recorded in the audit log)
                        </span>
                    </Label>
                    <Textarea
                        id="ot-remove-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder='e.g. "Shift cancelled" · "Entered against wrong employee"'
                        data-testid="ot-remove-reason"
                        aria-invalid={!reasonValid}
                    />
                    {!reasonValid && (
                        <div
                            className="text-[11px] text-rose-600"
                            data-testid="ot-remove-reason-error"
                        >
                            {reason.trim().length === 0
                                ? "Add a reason (min 5 characters) to continue."
                                : `Reason needs at least 5 characters (${reason.trim().length}/5).`}
                        </div>
                    )}
                </div>

                {periodLocked && (
                    <div
                        className="rounded-md border bg-amber-50 border-amber-200 p-2 text-xs text-amber-900"
                        data-testid="ot-remove-period-warning"
                    >
                        {isSuperAdmin ? (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={override}
                                    onChange={(e) => setOverride(e.target.checked)}
                                    data-testid="ot-remove-override"
                                />
                                <span>
                                    This row is in an <strong>approved</strong> payroll period.
                                    Tick to force-remove as Super Admin (audit action:{" "}
                                    <span className="mono">attendance_ot_removed_super_admin_override</span>
                                    ).
                                </span>
                            </label>
                        ) : (
                            <>Period is <strong>approved</strong>. Only a Super Admin can remove OT after approval.</>
                        )}
                    </div>
                )}

                <DialogFooter className="flex flex-col items-end gap-1">
                    {!canSubmit && _why() && (
                        <div
                            className="text-[11px] text-rose-600 self-end"
                            data-testid="ot-remove-apply-blocked-hint"
                        >
                            {_why()}
                        </div>
                    )}
                    <div className="flex gap-2 w-full justify-end">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            disabled={busy}
                            data-testid="ot-remove-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                            onClick={removeAll}
                            disabled={!canSubmit || entries.length === 0}
                            data-testid="ot-remove-all"
                        >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                            {busy && !activeId ? "Removing…" : `Remove all (${entries.length})`}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
